import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import { Button, Checkbox, Flex, Grid, Skeleton, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { useContext } from "react";

import { SIZES } from "../../theme/consts/sizing-consts";
import { ColumnDefinition, ColumnOrdering, TableContext } from "../rana-table";

import { TableCell } from "./table-cell";

export function TableHeader() {
  const {
    isSkeleton,
    columnDefinitions,
    ordering,
    setOrdering,
    templateColumns,
    allChecked,
    someChecked,
    setAllChecked,
    hasSelection,
    tableActions,
    checkedRows,
    hasAccordion,
  } = useContext(TableContext);

  const createOnClickForColumn = (column: ColumnDefinition) => {
    if (!column.orderingField || !setOrdering) return;
    return () => {
      if (!column.orderingField || !setOrdering) return;
      const current: ColumnOrdering = ordering ? ordering[column.orderingField] : undefined;

      if (!current) {
        setOrdering(column.orderingField, "az");
      } else if (current === "az") {
        setOrdering(column.orderingField, "za");
      } else {
        setOrdering(column.orderingField, undefined);
      }
    };
  };

  const spacing = SIZES[4];

  const filteredTemplateColumns = someChecked
    ? `${templateColumns.split(" ")[0]} 1fr`
    : templateColumns;

  return (
    <Grid
      as={isSkeleton ? Skeleton : Thead}
      bg="brand.cloudy"
      mt={spacing}
      padding={`0 ${spacing}`}
      m={0}
      roundedBottom="none"
      borderBottomWidth="base"
      borderColor="brand.border"
    >
      <Grid
        as={isSkeleton ? Skeleton : Tr}
        w="full"
        templateColumns={filteredTemplateColumns}
        h={11}
      >
        {hasSelection ? (
          <TableCell as={isSkeleton ? Skeleton : Th}>
            <Checkbox
              isChecked={allChecked}
              isIndeterminate={!allChecked && someChecked}
              onChange={setAllChecked}
            />
          </TableCell>
        ) : null}

        {hasAccordion ? <TableCell as={isSkeleton ? Skeleton : Th} /> : null}

        {someChecked ? (
          <TableCell as={isSkeleton ? Skeleton : Th} overflowX="auto">
            {tableActions.map(({ label, action, icon, getDisabledText, ...rest }) => {
              const disabledText = getDisabledText && getDisabledText(checkedRows);
              return (
                <Button
                  key={label}
                  flexShrink={0}
                  h={8}
                  variant="secondary"
                  onClick={() => action(checkedRows)}
                  leftIcon={icon}
                  mr={4}
                  {...(disabledText ? { title: disabledText, isDisabled: true } : {})}
                  {...rest}
                >
                  {label}
                </Button>
              );
            })}
          </TableCell>
        ) : (
          columnDefinitions.map((column, idx) => {
            const columnTitle =
              column.title ?? (column.titleRenderFunction && column.titleRenderFunction()) ?? "";

            return (
              <TableCell
                as={isSkeleton ? Skeleton : Th}
                key={`title-${idx}`}
                cursor={column.orderingField ? "pointer" : undefined}
                onClick={createOnClickForColumn(column)}
                title={typeof columnTitle === "string" ? columnTitle : undefined}
                textAlign={column.align ?? "left"}
                color="brand.clear_creek"
              >
                {column.orderingField && ordering && (
                  <>
                    {ordering[column.orderingField] === "az" && <TriangleDownIcon mr={1} />}
                    {ordering[column.orderingField] === "za" && <TriangleUpIcon mr={1} />}
                    {!ordering[column.orderingField] && (
                      <Flex flexDir="column" alignItems="center" mr={1} w={4}>
                        <TriangleUpIcon boxSize={2} />
                        <TriangleDownIcon boxSize={2} />
                      </Flex>
                    )}
                  </>
                )}
                <Text textStyle="sm" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                  {columnTitle}
                </Text>
              </TableCell>
            );
          })
        )}
      </Grid>
    </Grid>
  );
}
